import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import i18n from "@/i18n";
import { showWarning } from "@/services/notificationService";

/**
 * Gerenciador de sessão do frontend.
 *
 * Resolve o problema de o usuário permanecer com dados sensíveis na tela após
 * o token expirar. O backend expira o access token em 15 min e devolve 401,
 * mas sem um "relógio de sessão" no cliente nada desloga o usuário parado até
 * a próxima requisição. Este serviço adiciona duas garantias baseadas em tempo:
 *
 * 1. Refresh proativo — renova o access token pouco antes do `exp`, mantendo a
 *    sessão viva de forma transparente enquanto o refresh token (24 h) valer.
 * 2. Idle timeout (padrão OWASP para dado financeiro) — após ~25 min sem
 *    interação do usuário, avisa 1 min antes e então desloga, limpando a tela.
 *
 * O aviso é exibido via {@link SessionTimeoutDialog}, que lê os refs abaixo.
 */

// Limite de inatividade antes do logout automático.
const IDLE_LIMIT_MS = 25 * 60 * 1000;
// Antecedência com que o aviso de expiração aparece.
const WARNING_BEFORE_MS = 60 * 1000;
// Antecedência com que o token é renovado antes do `exp`.
const REFRESH_SKEW_MS = 60 * 1000;
// Espera antes de tentar o refresh de novo após uma falha transitória (rede/servidor).
const REFRESH_RETRY_MS = 30 * 1000;
// Não registra cada micro-movimento como atividade (economia de trabalho).
const ACTIVITY_THROTTLE_MS = 5000;
// Frequência da checagem de inatividade (deixa a contagem regressiva fluida).
const IDLE_CHECK_INTERVAL_MS = 1000;
// Canal para sincronizar atividade entre abas (evita que uma aba ociosa
// desloge outra que está em uso ativo, já que o localStorage é compartilhado).
const ACTIVITY_CHANNEL_NAME = "ori-session-activity";

const ACTIVITY_EVENTS = [
	"mousemove",
	"mousedown",
	"keydown",
	"scroll",
	"touchstart",
	"click",
] as const;

// Estado reativo consumido pelo diálogo de aviso.
export const sessionWarningVisible = ref(false);
export const sessionWarningSeconds = ref(0);

let lastActivity = Date.now();
let started = false;
let idleCheckTimer: ReturnType<typeof setInterval> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let activityChannel: BroadcastChannel | null = null;

/** Extrai o `exp` (em ms) do payload de um JWT, ou null se não for possível. */
function decodeJwtExp(token: string): number | null {
	try {
		const payload = token.split(".")[1];
		if (!payload) return null;
		const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
		const json = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
				.join("")
		);
		const data = JSON.parse(json);
		return typeof data.exp === "number" ? data.exp * 1000 : null;
	} catch {
		return null;
	}
}

/** Agenda a próxima renovação do access token com base no seu `exp`. */
function scheduleRefresh(): void {
	if (refreshTimer) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}

	const token = localStorage.getItem("token");
	if (!token) return;

	const expMs = decodeJwtExp(token);
	if (!expMs) return;

	const delay = expMs - Date.now() - REFRESH_SKEW_MS;
	if (delay <= 0) {
		void doRefresh();
	} else {
		refreshTimer = setTimeout(() => void doRefresh(), delay);
	}
}

/** Renova o token e reagenda a próxima renovação. */
async function doRefresh(): Promise<void> {
	const authStore = useAuthStore();
	const ok = await authStore.refreshAccessToken();
	if (ok) {
		scheduleRefresh();
	} else if (started && localStorage.getItem("refreshToken")) {
		// Falha transitória (rede/servidor): o refresh token ainda está no storage,
		// ou seja, não foi uma falha de auth (essa já teria deslogado e limpado o
		// storage). Tenta de novo em breve, sem encerrar a sessão.
		if (refreshTimer) clearTimeout(refreshTimer);
		refreshTimer = setTimeout(() => void doRefresh(), REFRESH_RETRY_MS);
	}
}

/** Marca o instante da última atividade e cancela o aviso de expiração pendente. */
function registerActivity(at: number): void {
	if (at > lastActivity) lastActivity = at;
	if (sessionWarningVisible.value) sessionWarningVisible.value = false;
}

/** Atividade local do usuário (com throttle); propaga para as outras abas. */
function onActivity(): void {
	const now = Date.now();
	if (now - lastActivity < ACTIVITY_THROTTLE_MS) return;
	registerActivity(now);
	activityChannel?.postMessage(now);
}

/** Atividade recebida de outra aba: mantém esta aba viva se há uso em qualquer uma. */
function onChannelActivity(event: MessageEvent): void {
	if (typeof event.data === "number") registerActivity(event.data);
}

/** Checa a inatividade a cada tick: exibe aviso, ou desloga ao estourar. */
function checkIdle(): void {
	const idleMs = Date.now() - lastActivity;

	if (idleMs >= IDLE_LIMIT_MS) {
		handleIdleLogout();
		return;
	}

	const remaining = IDLE_LIMIT_MS - idleMs;
	if (remaining <= WARNING_BEFORE_MS) {
		sessionWarningVisible.value = true;
		sessionWarningSeconds.value = Math.ceil(remaining / 1000);
	} else if (sessionWarningVisible.value) {
		sessionWarningVisible.value = false;
	}
}

/** Desloga por inatividade, avisando o usuário e limpando a tela. */
function handleIdleLogout(): void {
	stopSessionManager();
	showWarning(i18n.global.t("session.expiredByInactivity"));
	void useAuthStore().logout();
}

/**
 * Reinicia a contagem de inatividade e garante o token fresco.
 * Chamado pelo botão "Continuar conectado" do aviso.
 */
export function continueSession(): void {
	const now = Date.now();
	registerActivity(now);
	// Propaga para as outras abas: se o usuário optou por continuar aqui, uma aba
	// ociosa não deve deslogar (e limpar o localStorage compartilhado) por conta própria.
	activityChannel?.postMessage(now);
	scheduleRefresh();
}

/** Inicia o monitoramento de sessão (idempotente). Chamar ao logar. */
export function startSessionManager(): void {
	if (started) return;
	started = true;
	lastActivity = Date.now();
	sessionWarningVisible.value = false;

	ACTIVITY_EVENTS.forEach((event) =>
		window.addEventListener(event, onActivity, { passive: true })
	);
	if (typeof BroadcastChannel !== "undefined") {
		activityChannel = new BroadcastChannel(ACTIVITY_CHANNEL_NAME);
		activityChannel.addEventListener("message", onChannelActivity);
	}
	idleCheckTimer = setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS);
	scheduleRefresh();
}

/** Encerra o monitoramento e limpa timers/listeners. Chamar ao deslogar. */
export function stopSessionManager(): void {
	if (!started) return;
	started = false;

	ACTIVITY_EVENTS.forEach((event) =>
		window.removeEventListener(event, onActivity)
	);
	if (activityChannel) {
		activityChannel.removeEventListener("message", onChannelActivity);
		activityChannel.close();
		activityChannel = null;
	}
	if (idleCheckTimer) {
		clearInterval(idleCheckTimer);
		idleCheckTimer = null;
	}
	if (refreshTimer) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}
	sessionWarningVisible.value = false;
}
